import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { courseService } from '../services/api';
import { Course } from '../types/course';

interface HoleScore {
  hole: number;
  score: number;
  putts?: number;
  fairwayHit?: boolean;
  greenInRegulation?: boolean;
}

export default function NewRoundPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [currentHole, setCurrentHole] = useState(1);
  const [roundData, setRoundData] = useState({
    date: new Date().toISOString().split('T')[0],
    teeType: 'White',
    weather: 'Sunny',
    notes: ''
  });
  const [holeScores, setHoleScores] = useState<HoleScore[]>([]);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const data = await courseService.getAllCourses();
      setCourses(data);
      if (data.length > 0) {
        setSelectedCourse(data[0]);
        initializeHoleScores(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const initializeHoleScores = (course: Course) => {
    const scores = course.holes.map(hole => ({
      hole: hole.number,
      score: hole.par,
      putts: 2,
      fairwayHit: true,
      greenInRegulation: true
    }));
    setHoleScores(scores);
  };

  const handleCourseChange = (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setSelectedCourse(course);
      initializeHoleScores(course);
    }
  };

  const updateHoleScore = (hole: number, field: keyof HoleScore, value: any) => {
    setHoleScores(prev => prev.map(h => 
      h.hole === hole ? { ...h, [field]: value } : h
    ));
  };

  const getTotalScore = () => {
    return holeScores.reduce((sum, hole) => sum + hole.score, 0);
  };

  const getTotalPutts = () => {
    return holeScores.reduce((sum, hole) => sum + (hole.putts || 0), 0);
  };

  const getFairwaysHit = () => {
    return holeScores.filter(h => h.fairwayHit).length;
  };

  const getGreensInRegulation = () => {
    return holeScores.filter(h => h.greenInRegulation).length;
  };

  const handleSubmit = async () => {
    if (!selectedCourse) return;
    
    setLoading(true);
    try {
      // TODO: Submit round to API
      console.log('Submitting round:', {
        courseId: selectedCourse.id,
        ...roundData,
        scores: holeScores,
        totalScore: getTotalScore()
      });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      navigate('/rounds');
    } catch (error) {
      console.error('Failed to save round:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedCourse) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⛳</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">No Courses Available</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to add a course before recording a round
          </p>
          <Link 
            to="/courses/new"
            className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 inline-block shadow-lg hover:shadow-xl"
          >
            Add a Course
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-8">New Round</h1>

          {/* Round Setup */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Round Details</h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Course
                </label>
                <select
                  value={selectedCourse.id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                >
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={roundData.date}
                  onChange={(e) => setRoundData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tee Type
                </label>
                <select
                  value={roundData.teeType}
                  onChange={(e) => setRoundData(prev => ({ ...prev, teeType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                >
                  <option>Black</option>
                  <option>Blue</option>
                  <option>White</option>
                  <option>Red</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Weather
                </label>
                <select
                  value={roundData.weather}
                  onChange={(e) => setRoundData(prev => ({ ...prev, weather: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                >
                  <option>Sunny</option>
                  <option>Cloudy</option>
                  <option>Rainy</option>
                  <option>Windy</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (Optional)
              </label>
              <textarea
                value={roundData.notes}
                onChange={(e) => setRoundData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                placeholder="Course conditions, playing partners, etc..."
              />
            </div>
          </div>

          {/* Scorecard */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">Scorecard</h2>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Par {selectedCourse.par} • {selectedCourse.holes.length} holes
              </div>
            </div>

            {/* Hole Navigation */}
            <div className="mb-4 flex items-center justify-between">
              <button
                onClick={() => setCurrentHole(Math.max(1, currentHole - 1))}
                disabled={currentHole === 1}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                ← Previous
              </button>
              <span className="text-lg font-bold">Hole {currentHole}</span>
              <button
                onClick={() => setCurrentHole(Math.min(selectedCourse.holes.length, currentHole + 1))}
                disabled={currentHole === selectedCourse.holes.length}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                Next →
              </button>
            </div>

            {/* Current Hole Details */}
            {selectedCourse.holes.find(h => h.number === currentHole) && (
              <div className="border-t dark:border-gray-700 pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Score
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={holeScores.find(h => h.hole === currentHole)?.score || 0}
                      onChange={(e) => updateHoleScore(currentHole, 'score', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 text-center text-xl font-bold"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                      Par {selectedCourse.holes.find(h => h.number === currentHole)?.par}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Putts
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={holeScores.find(h => h.hole === currentHole)?.putts || 0}
                      onChange={(e) => updateHoleScore(currentHole, 'putts', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 text-center"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fairway Hit
                    </label>
                    <select
                      value={holeScores.find(h => h.hole === currentHole)?.fairwayHit ? 'yes' : 'no'}
                      onChange={(e) => updateHoleScore(currentHole, 'fairwayHit', e.target.value === 'yes')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      GIR
                    </label>
                    <select
                      value={holeScores.find(h => h.hole === currentHole)?.greenInRegulation ? 'yes' : 'no'}
                      onChange={(e) => updateHoleScore(currentHole, 'greenInRegulation', e.target.value === 'yes')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-golf-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
                    >
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Score Summary */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Front 9 */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Front 9</h3>
                <div className="grid grid-cols-9 gap-1 text-xs">
                  {holeScores.slice(0, 9).map((hole, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-gray-500 dark:text-gray-400">{idx + 1}</div>
                      <div className="font-bold">{hole.score}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-right font-bold">
                  Total: {holeScores.slice(0, 9).reduce((sum, h) => sum + h.score, 0)}
                </div>
              </div>

              {/* Back 9 */}
              {selectedCourse.holes.length > 9 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Back 9</h3>
                  <div className="grid grid-cols-9 gap-1 text-xs">
                    {holeScores.slice(9, 18).map((hole, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-gray-500 dark:text-gray-400">{idx + 10}</div>
                        <div className="font-bold">{hole.score}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-right font-bold">
                    Total: {holeScores.slice(9, 18).reduce((sum, h) => sum + h.score, 0)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Round Summary */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Round Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Score</p>
                <p className="text-3xl font-bold text-golf-600 dark:text-golf-400">{getTotalScore()}</p>
                <p className="text-xs text-gray-500">
                  {getTotalScore() - selectedCourse.par > 0 ? '+' : ''}{getTotalScore() - selectedCourse.par}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Putts</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{getTotalPutts()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Fairways Hit</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  {getFairwaysHit()}/{selectedCourse.holes.length}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">GIR</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {getGreensInRegulation()}/{selectedCourse.holes.length}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-end">
            <Link
              to="/rounds"
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </Link>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 disabled:from-gray-400 disabled:to-gray-400 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {loading ? 'Saving...' : 'Save Round'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}